<template>
  <div class="tabs">
    <div ref="tabTitlesWrapper" class="tabs__titles">
      <div
        v-for="(tab, idx) in tabs"
        :key="idx"
        ref="tabTitles"
        class="tabs__titles__title"
        :class="{
          'tabs__titles__title--focused': value === idx,
          'tabs__titles__title--focused-to-left': value === idx - 1,
          'tabs__titles__title--focused-to-right': value === idx + 1,
        }"
        @click="$emit('input', idx)"
      >
        <div class="tabs__titles__title__text">{{ tab }}</div>
      </div>
      <slot name="afterTabs" />
    </div>
    <div
      class="tabs__content-wrapper"
      :class="{
        'tabs__content-wrapper--focus-left': value === 0,
        'tabs__content-wrapper--focus-right': value === tabs.length - 1,
      }"
    >
      <slot />
    </div>
  </div>
</template>

<script>
/**
 * Component displaying a tab switcher and a slot
 *
 * @vue-prop {Number} value - Index of the focused tab
 * @vue-prop {Array} tabs - Array of strings (tab names)
 *
 * @vue-event input - Emit `value` (Number), triggered when a tab is focused
 */

import $ from 'jquery';

export default {

  name: 'Tabs',

  props: {
    value: {
      type: Number,
      required: true,
    },
    tabs: {
      type: Array,
      required: true,
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";

.tabs {
  display: flex;
  flex-flow: column;

  --tabscolor: #{$color-gray-background};
  --tabs-hover-color: #{$color-body-background};
  --tabs-text-color: inherit;
  --tabs-focused-text-color: inherit;


  &__titles {
    position: relative;
    line-height: 1.3rem;
    display: flex;

    &__title {
      flex: 1;
      text-align: center;
      white-space: nowrap;
      cursor: pointer;
      transition: background .2s;
      color: var(--tabs-text-color);
      background-color: var(--tabscolor);
      border-top-left-radius: $main-border-radius;
      border-top-right-radius: $main-border-radius;

      &--focused {
        font-weight: 600;
        border-radius: 50px;

        .tabs__titles__title__text {
          color: var(--tabs-focused-text-color);
          background-color: var(--tabscolor);
          border-top-left-radius: $main-border-radius;
          border-top-right-radius: $main-border-radius;
        }
      }

      &__text {
        background: #fff;
        padding: 0.75em 1.5em 0.75em 1.5em;
      }

      &:hover:not(.tabs__titles__title--focused) {
        border-radius: 50px;

        .tabs__titles__title__text {
          background: var(--tabs-hover-color);
          border-radius: $main-border-radius;
        }
      }

      &--focused-to-left {
        &:hover:not(.tabs__titles__title--focused) {
          border-bottom-left-radius: 0px;
        }

        .tabs__titles__title__text {
          border-bottom-left-radius: $main-border-radius;
        }
      }

      &--focused-to-right {
        &:hover:not(.tabs__titles__title--focused) {
          border-bottom-right-radius: 0px;
        }

        .tabs__titles__title__text {
          border-bottom-right-radius: $main-border-radius;
        }
      }
    }
  }

  &__content-wrapper {
    flex: 1;
    background-color: var(--tabscolor);
    overflow: auto;
    border-radius: $main-border-radius;

    &--focus-left {
      border-top-left-radius: 0px;
    }

    &--focus-right {
      border-top-right-radius: 0px;
    }
  }
}

@media (max-width: $smartphone-portrait) {
  .tabs {
    &__titles {
      &__title {
        &__text {
          padding: 0.5em 1.2em 0.65em 1.2em;
        }
      }
    }
  }
}
</style>
